import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router';
import { AppBar, Toolbar, Typography, Box, IconButton, Menu } from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Group as GroupIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import { ProfileSection } from './profile-section';

import { paths } from '@/config/paths';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: '#D7092F',
  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(0.5),
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.8,
  },
  transition: 'opacity 0.2s ease-in-out',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const LogoImage = styled('img')(({ theme }) => ({
  height: '14px',
  width: 'auto',
  [theme.breakpoints.down('md')]: {
    height: '12px',
  },
}));

const AppTitle = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontWeight: 'bold',
  fontSize: '1.2rem',
  lineHeight: 1,
  [theme.breakpoints.down('md')]: {
    fontSize: '1.2rem',
  },
}));

const NavigationMenu = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginLeft: theme.spacing(4),
  [theme.breakpoints.down('lg')]: {
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(2),
  },
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const NavLink = styled(RouterLink)(({ theme }) => ({
  color: '#FFFFFF',
  textDecoration: 'none',
  fontSize: '0.9rem',
  fontWeight: 500,
  padding: theme.spacing(1, 2),
  borderRadius: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  minHeight: '32px',
  justifyContent: 'center',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    textDecoration: 'none',
  },
  '&.active': {
    backgroundColor: '#FFFFFF',
    color: '#D7092F',
    '&:hover': {
      backgroundColor: '#FFFFFF',
      color: '#D7092F',
    },
  },
  [theme.breakpoints.down('lg')]: {
    padding: theme.spacing(0.5, 1.5),
    fontSize: '0.8rem',
    minHeight: '28px',
  },
}));

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('md')]: {
    display: 'flex',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      transform: 'scale(1.05)',
    },
    transition: 'all 0.2s ease-in-out',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    borderRadius: '100%',
    padding: theme.spacing(1),
    minWidth: '40px',
    minHeight: '40px',
    marginLeft: theme.spacing(1),
  },
}));

const MobileNavLink = styled(RouterLink)(({ theme }) => ({
  color: theme.palette.text.primary,
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 2),
  width: '92%',
  borderRadius: theme.spacing(3),
  margin: theme.spacing(0.5, 1),
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    textDecoration: 'none',
  },
  '&.active': {
    backgroundColor: '#E8F0FE',
    color: '#202124',
    '&:hover': {
      backgroundColor: '#E8F0FE',
    },
  },
}));

const navigationItems = [
  { label: '予定一覧', icon: ScheduleIcon, path: paths.plans.path, showOnMobile: true },
  { label: '現場一覧', icon: WorkIcon, path: paths.properties.path, showOnMobile: true },
  { label: '会社一覧', icon: BusinessIcon, path: paths.companies.path, showOnMobile: false },
  { label: '帳票出力', icon: DescriptionIcon, path: paths.reports.path, showOnMobile: false },
  { label: '担当者一覧', icon: GroupIcon, path: paths.members.path, showOnMobile: false },
];

export const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const mobileMenuOpen = Boolean(mobileMenuAnchor);

  const handleMobileMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleLogoClick = () => {
    navigate(paths.plans.path);
  };

  return (
    <StyledAppBar position='static'>
      <Toolbar>
        {/* Logo and App Title - Desktop only */}
        <LogoContainer onClick={handleLogoClick}>
          <LogoImage src='/logo_BuildApp_admin--white.svg' alt='BuildApp Logo' />
          <AppTitle variant='h6'>生コンデリバリー</AppTitle>
        </LogoContainer>

        {/* Navigation Menu - Desktop */}
        <NavigationMenu>
          {navigationItems.map((item) => {
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={isActive(item.path) ? 'active' : ''}
              >
                {item.label}
              </NavLink>
            );
          })}
        </NavigationMenu>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Right Section - Profile (Desktop and Mobile) */}
        <ProfileSection />

        {/* Mobile Menu Button - Only visible on mobile, positioned on the right */}
        <MobileMenuButton onClick={handleMobileMenuClick} color='inherit' aria-label='menu'>
          <MenuIcon />
        </MobileMenuButton>

        {/* Mobile Navigation Menu */}
        <Menu
          id='mobile-menu'
          anchorEl={mobileMenuAnchor}
          open={mobileMenuOpen}
          onClose={handleMobileMenuClose}
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                minWidth: 200,
                borderRadius: 2,
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {navigationItems
            .filter((item) => item.showOnMobile)
            .map((item) => {
              return (
                <MobileNavLink
                  key={item.path}
                  to={item.path}
                  className={isActive(item.path) ? 'active' : ''}
                  onClick={handleMobileMenuClose}
                >
                  {item.label}
                </MobileNavLink>
              );
            })}
        </Menu>
      </Toolbar>
    </StyledAppBar>
  );
};
